/**
 * Browser half of the Mufid-only managed Zoom creation seam.
 *
 * Zoom Server-to-Server OAuth is a backend capability. The browser sends one
 * same-origin request and retains no provider credential, callback state, or
 * provider identity.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { sessionPortalApi } from "@/lib/api/sessionPortal";
import { ApiError } from "@/lib/api/client";

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return typeof error.detail === "string" ? error.detail : "Zoom meeting creation could not be completed.";
  }
  return "Zoom meeting creation could not be completed.";
}

export function ZoomMeetingControl({
  sessionId,
  replace,
  variant = "outline",
  size = "default",
  className,
  onCompleted,
  onRecoveryRequired,
}: {
  sessionId: string;
  replace: boolean;
  variant?: "default" | "outline";
  size?: "default" | "lg";
  className?: string;
  onCompleted: () => void;
  onRecoveryRequired: () => void;
}) {
  const [working, setWorking] = useState(false);
  const [recoveryPending, setRecoveryPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin() {
    setError(null);
    setWorking(true);
    try {
      await sessionPortalApi.createZoomMeeting(sessionId, { replace });
      onCompleted();
    } catch (completionError) {
      setError(errorMessage(completionError));
      // A failed response can still follow a provider write. Freeze this
      // control until the owner refreshes the canonical workspace projection;
      // its managed_zoom state decides whether retry is safe.
      setRecoveryPending(true);
      onRecoveryRequired();
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="grid gap-2">
      <Button
        type="button"
        size={size}
        variant={variant}
        className={className}
        onClick={() => void begin()}
        disabled={working || recoveryPending}
      >
        {working && <Loader2 aria-hidden className="mr-1.5 size-4 animate-spin" />}
        {replace ? "Replace Zoom meeting" : "Create Zoom meeting"}
      </Button>
      {error && <p role="status" className="text-ui text-destructive">{error}</p>}
    </div>
  );
}
